<script setup>
import dayjs from 'dayjs'
import { computed, ref, provide } from 'vue'

import { CHECKIN_MODAL_ENTRY_POINTS } from '@/utils/check-ins/check-ins'
import { PROGRESS_MODULE_CELL_PLACEMENTS } from '@/utils/custom-dashboard-helper'
import { getStartOfTheDay, utcDateToLocal } from '@/utils/date'
import { getProgressTableHeaderCellDisplayValue } from '@/utils/element-progress'
import { TARGET_PERIOD_ENTITY_KEYS } from '@/utils/entity-keys'
import { okrElementFormProgressTableStateInjectionKey } from '@/utils/injection-keys'
import {
  currentUserCanReadObjective,
  currentUserCanUpdateObjective,
  isOkrElementClosed,
  okrElementGradeIsEditable
} from '@/utils/objectives'
import { METRIC_VALUES_KEYS } from '@/utils/okr-element-values'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

import { ELEMENT_PROGRESS_TABLE_HEADER_CELL_TEST_ID } from '@/components/check-ins/jest-helpers'
import ProgressModuleGridCell from '@/components/dashboard/custom-dashboard/modules/progress-module/ProgressModuleGridCell'

defineOptions({
  name: 'ElementProgressTableColumn',
  inheritAttrs: false
})

const props = defineProps({
  headerData: {
    type: Object,
    required: true
  },

  data: {
    type: Object,
    required: true
  },

  progress: {
    type: Object,
    required: true
  },

  element: {
    type: Object,
    required: true
  }
})

const updatable = computed(() => {
  return (
    currentUserCanUpdateObjective(props.element) &&
    okrElementGradeIsEditable(props.element) &&
    !isOkrElementClosed(props.element)
  )
})

const hasReadAccess = computed(() => currentUserCanReadObjective(props.element))

const valueUpdatable = computed(() => {
  const today = getStartOfTheDay(dayjs())

  const cellStartDate = getStartOfTheDay(
    dayjs(props.headerData[TARGET_PERIOD_ENTITY_KEYS.START_DATE])
  )

  const isFuturePeriod = Number(cellStartDate) > Number(today)

  return !isFuturePeriod && updatable.value
})

const onAddValueButtonClick = () => {
  if (!valueUpdatable.value) return

  emit('open-checkin-modal', {
    okrElement: {
      ...props.element,
      [METRIC_VALUES_KEYS.CURRENT_VALUE]: props.data.value
    },
    entryPoint: CHECKIN_MODAL_ENTRY_POINTS.CREATE,
    presetBackdate: utcDateToLocal(props.headerData[TARGET_PERIOD_ENTITY_KEYS.END_DATE]) || null
  })
}

const emit = defineEmits({
  'value-updated': null,
  'threshold-updated': null,
  'open-checkin-modal': null,
  loading: null
})

const columnState = ref({
  openCheckinModal: onAddValueButtonClick,
  onElementPeriodThresholdUpdated: payload => {
    emit('threshold-updated', payload)
  }
})

provide(okrElementFormProgressTableStateInjectionKey, columnState)
</script>

<template>
  <div class="eptc-Column">
    <div
      :data-auto-testid="ELEMENT_PROGRESS_TABLE_HEADER_CELL_TEST_ID"
      :data-testid="ELEMENT_PROGRESS_TABLE_HEADER_CELL_TEST_ID"
      :style="{
        '--cell-padding-right': $slots['header-cell-append'] ? '0px' : undefined
      }"
      class="ept-Cell ept-Cell-header"
    >
      <span class="oboard-truncated-text">
        {{
          getProgressTableHeaderCellDisplayValue({
            cellData: headerData,
            granulationId: element[OKR_ELEMENT_KEYS.GRANULATION_ID]
          })
        }}
      </span>

      <span v-if="$slots['header-cell-append']" class="ept-Cell_Append">
        <slot name="header-cell-append" />
      </span>
    </div>

    <ProgressModuleGridCell
      :cell-data="{
        ...progress
      }"
      :element
      :has-read-access="hasReadAccess"
      :placement="PROGRESS_MODULE_CELL_PLACEMENTS.OKR_ELEMENT_FORM"
      style="--resolved-divider-size: 0px"
      @update:is-loading="emit('loading', $event)"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';

.eptc-Column {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: inherit;
  flex: 1;
  --pmcc-cell-padding: 10px 8px;
}

.ept-Cell {
  color: $dark-2;
  font-weight: fw('semi-bold');
  min-height: 32px;
  padding-inline: 16px var(--cell-padding-right, 6px);
  line-height: 16px;
  display: flex;
  align-items: center;
  background-color: $white;

  &-header {
    font-size: $fs-10;
  }
}

.ept-Cell_Append {
  padding-left: 6px;
  margin-left: auto;
  flex-shrink: 0;
}
</style>
